import dayjs from "dayjs";
import { DISEASE_RISK_CROPS, MELCAST_MODEL } from "../config/diseaseRisk.config";

export const isRiskDate = (value) => typeof value === "string" &&
  /^\d{4}-\d{2}-\d{2}$/.test(value) && dayjs(value).isValid() && dayjs(value).format("YYYY-MM-DD") === value;

export const formatRiskDate = (value) => isRiskDate(value) ? dayjs(value).format("DD/MM/YYYY") : "—";

export const resolveRiskCrop = (name = "") => {
  const tokens = String(name).normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().split(/[^a-z]+/);
  const matches = DISEASE_RISK_CROPS.filter(({ aliases }) => aliases.some((alias) => tokens.includes(alias)));
  return matches.length === 1 ? matches[0] : null;
};

export const getRiskCultivations = (field) => (Array.isArray(field?.field_colture) ? field.field_colture : [])
  .map((crop, index) => ({
    ...crop,
    key: String(crop.id ?? `${crop.colture?.id || crop.colture?.name || "crop"}:${crop.start_date}:${index}`),
    name: crop.colture?.name || "Coltura non specificata",
    riskCrop: resolveRiskCrop(crop.colture?.name),
  }))
  .sort((a, b) => String(b.start_date || "").localeCompare(String(a.start_date || "")));

export const getDefaultRiskCultivation = (cultivations, today = dayjs().format("YYYY-MM-DD")) =>
  cultivations.find((crop) => isRiskDate(crop.start_date) && crop.start_date <= today &&
    (!crop.end_date || (isRiskDate(crop.end_date) && crop.end_date >= today))) ||
  cultivations.filter((crop) => isRiskDate(crop.end_date) && crop.end_date < today)
    .sort((a, b) => b.end_date.localeCompare(a.end_date))[0] || cultivations[0] || null;

export const getRiskBounds = (crop, today = dayjs().format("YYYY-MM-DD")) => ({
  min: isRiskDate(crop?.start_date) ? crop.start_date : "",
  max: isRiskDate(crop?.end_date) && crop.end_date < today ? crop.end_date : today,
});

export const getRiskPeriod = (crop, days = 30, today) => {
  const { min, max } = getRiskBounds(crop, today);
  const start = dayjs(max).subtract(days - 1, "day").format("YYYY-MM-DD");
  return { from: min && start < min ? min : start, to: max };
};

export const validateRiskRequest = (request, crop, today) => {
  if (!request?.fieldUuid) return "UUID del terreno non disponibile.";
  if (!crop?.riskCrop?.models.includes(request.model)) return "Nessun modello disponibile per questa coltura.";
  if (!isRiskDate(request.from) || !isRiskDate(request.to)) return "Inserisci un intervallo di date valido.";
  const { min, max } = getRiskBounds(crop, today);
  if (request.from > request.to) return "La data iniziale deve precedere o coincidere con quella finale.";
  if ((min && request.from < min) || request.to > max) return "Il periodo deve rientrare nella coltivazione e non può includere date future.";
  if (request.model === MELCAST_MODEL) {
    if (!isRiskDate(request.seasonStartDate) || request.seasonStartDate >= request.to) {
      return "L’inizio stagione del modello deve precedere la data finale.";
    }
    if (request.seasonStartDate > request.from) return "Il periodo visualizzato deve iniziare dalla partenza del modello o da una data successiva.";
  }
  return null;
};

export const getRiskRequestKey = (request) => JSON.stringify([
  request.fieldUuid, request.model, request.from, request.to,
  request.model === MELCAST_MODEL ? request.seasonStartDate : null,
  request.model === MELCAST_MODEL ? request.melonType : null,
]);

export const getRiskDates = (from, to) => {
  if (!isRiskDate(from) || !isRiskDate(to) || from > to) return [];
  const dates = [];
  for (let date = dayjs(from); date.format("YYYY-MM-DD") <= to; date = date.add(1, "day")) {
    dates.push(date.format("YYYY-MM-DD"));
  }
  return dates;
};

const sortDays = (days) => [...new Map((Array.isArray(days) ? days : [])
  .filter((day) => isRiskDate(day?.date)).map((day) => [day.date, day])).values()]
  .sort((a, b) => a.date.localeCompare(b.date));

export const normalizeDiseaseRisk = (payload, request) => {
  const isMelcast = request.model === MELCAST_MODEL;
  if ((isMelcast && (!payload || Array.isArray(payload) || !Array.isArray(payload.daily_efi))) ||
      (!isMelcast && !Array.isArray(payload))) throw new Error("Risposta del modello non valida.");
  const roots = isMelcast ? [payload] : payload;
  if (roots.some((item) => item.field_uuid && item.field_uuid !== request.fieldUuid)) {
    throw new Error("La risposta del modello si riferisce a un altro terreno.");
  }
  let assessments;
  if (isMelcast) {
    let previousCount = 0;
    assessments = sortDays(payload.daily_efi).map((day) => {
      const thresholdReached = Number.isFinite(day.spray_recommendation) && day.spray_recommendation > previousCount;
      if (Number.isFinite(day.spray_recommendation)) previousCount = day.spray_recommendation;
      return { ...day, thresholdReached };
    });
  } else {
    assessments = sortDays(payload.map((item) => ({
      ...item,
      date: item.risk_date,
      ...(Array.isArray(item.daily_severity) ? { daily_severity: sortDays(item.daily_severity) } : {}),
      ...(Array.isArray(item.daily_downy) ? { daily_downy: sortDays(item.daily_downy) } : {}),
    })));
  }
  assessments = assessments.filter((item) => item.date >= request.from && item.date <= request.to);
  const byDate = new Map(assessments.map((item) => [item.date, item]));
  const points = getRiskDates(request.from, request.to).map((date) => ({
    date, cumulative_severity: null, n_sip_hours: null, n_germination_hours: null,
    cumulative_efi: null, efi: null, available: byDate.has(date), ...byDate.get(date),
  }));
  return {
    assessments, points, metadata: isMelcast ? payload : null,
    missingDates: points.filter((item) => !item.available).map((item) => item.date),
    expectedCount: points.length,
  };
};

export const isIncompleteRiskData = (error) => error?.response?.status === 422 &&
  /incomplete (weather|hourly) data|hourly data is incomplete|insufficient (weather|hourly) data|expected \d+ hourly readings/i
    .test(String(error.response.data?.detail || ""));

export const getRiskError = (error) => {
  const status = error?.response?.status;
  const detail = typeof error?.response?.data?.detail === "string" ? error.response.data.detail : null;
  let message = "Impossibile caricare il modello. Riprova.";
  if (status === 401 || status === 403) message = "Accesso ai modelli non autorizzato. Verifica la configurazione del servizio.";
  else if (status === 404) message = "Nessun sensore meteo configurato o disponibile per questo terreno.";
  else if (isIncompleteRiskData(error)) message = "Dati meteo insufficienti per calcolare le valutazioni richieste.";
  else if (status === 422) message = "Il modello non può essere calcolato: verifica le date e le misure meteo disponibili.";
  else if (status >= 500) message = "Il servizio dei modelli o dei sensori non è disponibile. Riprova più tardi.";
  else if (error?.code === "ECONNABORTED") message = "Il calcolo ha superato il tempo disponibile. Riprova.";
  else if (!error?.response && error?.request) message = "Impossibile contattare il servizio. Verifica la connessione.";
  else if (!error?.response && error?.message) message = error.message;
  return { message, detail };
};
